"use client";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { useFormContext } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

export const farmType = [
  { label: "dairy", value: "dairy" },
  { label: "beef", value: "beef" },
  { label: "poultry", value: "poultry" },
];

export const sex = [
  {
    label: "Male",
    sex: "Male",
  },
  {
    label: "Female",
    sex: "Female",
  },
];

export const statuses = [
  { label: "Inactive", uid: "inactive" },
  { label: "Active", uid: "active" },
  { label: "Semen", uid: "semen" },
  { label: "Embryo", uid: "embryo" },
  { label: "Unknown Status", uid: "unknown status" },
];

export const phases = [
  {
    label: "Phase 1",
    value: "phase1",
  },
  {
    label: "Phase 2",
    value: "phase2",
  },
  {
    label: "Phase 3",
    value: "phase3",
  },
];

export const frameSizes = [
  {
    label: "Small Frame",
    value: "Small Frame",
    description: "Less than 42 inches (107 cm) in height at the withers",
  },
  {
    label: "Medium Frame",
    value: "Medium Frame",
    description: "42-46 inches (107-117 cm) in height at the withers",
  },
  {
    label: "Large Frame",
    value: "Large Frame",
    description: "46-50 inches (117-127 cm) in height at the withers",
  },
  {
    label: "Extra Large Frame",
    value: "Extra Large Frame",
    description: "Over 50 inches (127 cm) in height at the withers",
  },
];

// New options for the new fields
export const cattleClasses = [
  { label: "Calf", value: "calf" },
  { label: "Heifer", value: "heifer" },
  { label: "Steer", value: "steer" },
  { label: "Cow", value: "cow" },
  { label: "Bull", value: "bull" },
  { label: "Ox", value: "ox" },
];

export const diagnosisOptions = [
  { label: "Healthy", value: "healthy" },
  { label: "Respiratory Disease", value: "respiratory" },
  { label: "Digestive Disorder", value: "digestive" },
  { label: "Parasitic Infection", value: "parasitic" },
  { label: "Lameness", value: "lameness" },
  { label: "Reproductive Issue", value: "reproductive" },
  { label: "Metabolic Disorder", value: "metabolic" },
  { label: "Other", value: "other" },
];

export const inseminationPrograms = [
  { label: "Natural Service", value: "natural" },
  { label: "Artificial Insemination", value: "artificial" },
  { label: "Embryo Transfer", value: "embryo" },
  { label: "Timed AI", value: "timed" },
  { label: "Sexed Semen", value: "sexed" },
  { label: "None", value: "none" },
];

export const farms = [
  { label: "Main Ranch", value: "main" },
  { label: "North Pasture", value: "north" },
  { label: "South Pasture", value: "south" },
  { label: "East Feedlot", value: "east" },
  { label: "West Feedlot", value: "west" },
];

export const supervisors = [
  { label: "John Smith", value: "john" },
  { label: "Maria Garcia", value: "maria" },
  { label: "Ahmed Hassan", value: "ahmed" },
  { label: "Li Wei", value: "li" },
  { label: "Oluwaseun Adebayo", value: "oluwaseun" },
];

export function StatusInfoForm() {
  const { control } = useFormContext();

  return (
    <div className="space-y-6 w-full ">
      <h2 className="text-2xl font-bold text-center mb-6">
        Status Information
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          control={control}
          name="animalStatus"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={"text-[#008c8fff]"}>
                Animal Status
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select animal status" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent
                  className={"overflow-y-auto z-50 shadow-lg bg-white"}
                >
                  {statuses.map((option: any) => (
                    <SelectItem key={option.uid} value={option.uid}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="birthWeight"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={"text-[#008c8fff]"}>
                Birth Weight (kg)
              </FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.1"
                  placeholder="Enter birth weight"
                  {...field}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value ? Number(e.target.value) : undefined,
                    )
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="farm"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={"text-[#008c8fff]"}>
                Farm Location
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select farm location" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent
                  className={"overflow-y-auto z-50 shadow-lg bg-white"}
                >
                  {farms.map((option: any) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="assignedSupervisor"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={"text-[#008c8fff]"}>
                Assigned Supervisor
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select supervisor" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent
                  className={"overflow-y-auto z-50 shadow-lg bg-white"}
                >
                  {supervisors.map((option: any) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="cattleClass"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={"text-[#008c8fff]"}>Cattle Class</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select cattle class" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent
                  className={"overflow-y-auto z-50 shadow-lg bg-white"}
                >
                  {cattleClasses.map((option: any) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="phases"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={"text-[#008c8fff]"}>
                Production Phase
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select production phase" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent
                  className={"overflow-y-auto z-50 shadow-lg bg-white"}
                >
                  {phases.map((option: any) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="diagnosis"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={"text-[#008c8fff]"}>
                Health Diagnosis
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select health diagnosis" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent
                  className={"overflow-y-auto z-50 shadow-lg bg-white"}
                >
                  {diagnosisOptions.map((option: any) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="inseminationProgram"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={"text-[#008c8fff]"}>
                Insemination Program
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select insemination program" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent
                  className={"overflow-y-auto z-50 shadow-lg bg-white"}
                >
                  {inseminationPrograms.map((option: any) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="numberOffspring"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={"text-[#008c8fff]"}>
                Number of Offspring
              </FormLabel>
              <FormControl>
                <Input
                  type="number"
                  placeholder="Enter number of offspring"
                  {...field}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value ? Number(e.target.value) : undefined,
                    )
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="indexEfficiency"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={"text-[#008c8fff]"}>
                Efficiency Index
              </FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Enter efficiency index"
                  {...field}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value ? Number(e.target.value) : undefined,
                    )
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="slaughterInfo"
          render={({ field }) => (
            <FormItem className="md:col-span-2">
              <FormLabel className={"text-[#008c8fff]"}>
                Slaughter Information
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter slaughter information if applicable"
                  className="min-h-[80px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="farmerID"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={"text-[#008c8fff]"}>Farmer ID</FormLabel>
              <FormControl>
                <Input placeholder="Enter farmer ID" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="farmID"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={"text-[#008c8fff]"}>Farm ID</FormLabel>
              <FormControl>
                <Input placeholder="Enter farm ID" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="url"
          render={({ field }) => (
            <FormItem className="md:col-span-2">
              <FormLabel className={"text-[#008c8fff]"}>Image URL</FormLabel>
              <FormControl>
                <Input placeholder="Enter image URL" {...field} />
              </FormControl>
              <p className="text-xs text-muted-foreground mt-1">
                URL to an image of the animal (optional)
              </p>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <Card className="p-4 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Status information helps track the current state of the animal and its
          ownership details. This information is important for administrative
          and management purposes.
        </p>
      </Card>
    </div>
  );
}
